/**
 * AllOrders
 */

// React
import React, { FunctionComponent, useEffect, useState, useContext } from 'react';
import { useLocation } from 'react-router-dom';
// Context
import { AuthConsumer } from '../../authContext';

// Redux
import { connect } from 'react-redux';
import { compose } from 'redux';
import {
  getOrdersAction,
  updateOrderStatusActions,
  updateAllOrderStatusActions,
  cancelOrderAction,
  getAllOrdersReportAction,
  changeDeliveryTypeAction,
  getPostOrdersAction,
} from './actions';
import { Store } from '../../redux/reducers';

// Selector
import { createStructuredSelector } from 'reselect';
import {
  makeSelectOrdersList,
  makeSelectLoading,
  makeSelectError,
  makeSelectCountList,
  makeSelectAllOrders,
} from './selectors';

// UI

import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import {
  Typography,
  Grid,
  Button,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Divider,
} from '@material-ui/core';

import Chart from 'react-google-charts';

// Icon
import Icon from '@mdi/react';
import {
  mdiAccountOutline,
  mdiPackageVariant,
  mdiStateMachine,
  mdiCalendar,
  mdiWarehouse,
  mdiCity,
  mdiKey,
  mdiTruck,
  mdiChevronLeft,
} from '@mdi/js';

// Components
import RenderFilter from '../../utils/listDaynamicFilterHookForm';
import type { Filter } from '../../utils/listDaynamicFilter';

// Helper
import { removeEmptyValueObject } from '../../utils/helpers';

// Type
import {
  Props,
  SelectorProps,
  Payload,
  cancelPayload,
  PayloadAllUpdate,
  PayloadChangeDeliveryType,
} from './interfaces';
import { mdiCityVariantOutline } from '@mdi/js/commonjs/mdi';

// apis

import handleError from '../../utils/handleError';
import ordersRepository from 'client-sdk/src/Repository/orders-repository';
import useQuery from '../../utils/hook/useQuery';
import CourierCards from './CourierCards';

// Style
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {},
    paper: {},
    loading: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      height: '50vh',
      alignSelf: 'center',
      justifyContent: 'center',
      background: '#ccc',
    },
    pagination: {
      // marginTop: theme.spacing(1),
    },
    searchBox: {
      display: 'flex',
      position: 'relative',
      backgroundColor: theme.palette.common.white,
      borderRadius: '4px',
      padding: '10px 0',
      boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.1)',
    },
    input: {
      display: 'flex',
      marginLeft: theme.spacing(1),
      flex: 2,
      '@media (max-width: 768px)': {
        fontSize: '12px',
      },
    },
    iconButton: {
      padding: 8,
      marginRight: '10px',
      position: 'relative',
      '&:before': {
        content: '""',
        position: 'absolute',
        width: '1px',
        height: '36px',
        top: '4px',
        left: '-9px',
        backgroundColor: '#EDF1F4',
      },
    },
    textArea: {
      width: '100%',
    },
    filterBox: {
      position: 'absolute',
      backgroundColor: '#fff',
      width: '100%',
      marginTop: '30px',
      top: '47px',
      right: 0,
      borderRadius: '0  0 4px  4px',
      padding: '16px 16px',
      boxSizing: 'border-box',
      boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.1)',
      zIndex: 10,
    },
    filterOpen: {
      display: 'block',
    },
    filterClose: {
      display: 'none',
    },
    paginationBox: {
      display: 'flex',
    },
    selectSize: {
      width: '35px',
    },
    subtitle: {
      fontSize: '14px',
      fontWeight: 500,
      color: '#8A8A8A',
    },
    magnifyIcon: {
      transform: 'rotate(-90deg)',
    },
    // Cancel Dialog
    cancelIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),

      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.error.main,
      },
    },
    cancelTitle: {
      fontSize: '18px',
      textAlign: 'center',
      fontWeight: 500,
      lineHeight: 2,
      marginBottom: theme.spacing(3),
    },
    actionsBox: {
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
    },
    editIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(2),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.blue.main,
      },
    },
    btnBox: {
      marginRight: '10px',
      display: 'flex',
      flexDirection: 'row-reverse',
      width: '100%',
      '@media (max-width: 768px)': {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
      },
    },

    typeSection: {
      textAlign: 'center',
      marginBottom: theme.spacing(2),
      '& .MuiFormGroup-row': {
        justifyContent: 'space-around',
      },
    },
    headerWidth: {
      '& .MuiCardHeader-avatar': {
        width: '100%',
      },
    },
    colorBullet: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      margin: '4px 8px 4px 2px',
    },
  })
);

type Inputs = {
  'customerNationalCode.contains': string;
};

const AllOrders: FunctionComponent<Props> = (props: Props) => {
  const [filters, setFilters] = useState([] as Filter[]);
  const { filtersValues, setFiltersValues } = useQuery({
    'fulfillmentWarehouseId.equals': 4,
    page: 0,
    size: 100,
  } as any);

  const [id, setId] = useState(-1 as number);

  const [loading, setLoading] = useState(false as boolean);

  const [cardCounts, setCardCounts] = useState({});
  const [countError, setCountError] = useState<string>('');

  const getingCounts = async (actions: any) => {
    const payload = { ...actions };
    try {
      setLoading(true);
      const { response: readyCount } = await ordersRepository.getOrdersCount({
        ...payload.readyCount,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: readyCountPost,
      } = await ordersRepository.getPostOrdersCount({
        ...payload.readyCountPost,
        'fulfillmentWarehouseId.equals': 4,
      });
      const { response: readyCountVip } = await ordersRepository.getOrdersCount({
        ...payload.readyCountVip,
        'fulfillmentWarehouseId.equals': 4,
      });
      const { response: createCount } = await ordersRepository.getOrdersCount({
        ...payload.createCount,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: createCountPost,
      } = await ordersRepository.getPostOrdersCount({
        ...payload.createCountPost,
        'fulfillmentWarehouseId.equals': 4,
      });
      const { response: createCountVip } = await ordersRepository.getOrdersCount({
        ...payload.createCountVip,
        'fulfillmentWarehouseId.equals': 4,
      });
      const { response: packagedCount } = await ordersRepository.getOrdersCount({
        ...payload.packagedCount,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: packagedCountPost,
      } = await ordersRepository.getPostOrdersCount({
        ...payload.packagedCountPost,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: packagedCountVip,
      } = await ordersRepository.getOrdersCount({
        ...payload.packagedCountVip,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierRegisteringCountTarof,
      } = await ordersRepository.getOrdersCount({
        ...payload.courierRegisteringCountTarof,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierRegisterdCountTarof,
      } = await ordersRepository.getOrdersCount({
        ...payload.courierRegisterdCountTarof,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierDeliveredCountTarof,
      } = await ordersRepository.getOrdersCount({
        ...payload.courierDeliveredCountTarof,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierDelivered3plCountTarof,
      } = await ordersRepository.getOrdersCount({
        ...payload.courierDelivered3plCountTarof,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierNotDeliverdCountTarof,
      } = await ordersRepository.getOrdersCount({
        ...payload.courierNotDeliverdCountTarof,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierReturnedCountTarof,
      } = await ordersRepository.getOrdersCount({
        ...payload.courierReturnedCountTarof,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierRegisteringCountPetro,
      } = await ordersRepository.getOrdersCount({
        ...payload.courierRegisteringCountPetro,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierRegisterdCountPetro,
      } = await ordersRepository.getOrdersCount({
        ...payload.courierRegisterdCountPetro,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierDeliveredCountPetro,
      } = await ordersRepository.getOrdersCount({
        ...payload.courierDeliveredCountPetro,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierDelivered3plCountPetro,
      } = await ordersRepository.getOrdersCount({
        ...payload.courierDelivered3plCountPetro,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierNotDeliverdCountPetro,
      } = await ordersRepository.getOrdersCount({
        ...payload.courierNotDeliverdCountPetro,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierReturnedCountPetro,
      } = await ordersRepository.getOrdersCount({
        ...payload.courierReturnedCountPetro,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierRegisteringCountLink,
      } = await ordersRepository.getOrdersCount({
        ...payload.courierRegisteringCountLink,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierRegisterdCountLink,
      } = await ordersRepository.getOrdersCount({
        ...payload.courierRegisterdCountLink,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierDeliveredCountLink,
      } = await ordersRepository.getOrdersCount({
        ...payload.courierDeliveredCountLink,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierDelivered3plCountLink,
      } = await ordersRepository.getOrdersCount({
        ...payload.courierDelivered3plCountLink,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierNotDeliverdCountLink,
      } = await ordersRepository.getOrdersCount({
        ...payload.courierNotDeliverdCountLink,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierReturnedCountLink,
      } = await ordersRepository.getOrdersCount({
        ...payload.courierReturnedCountLink,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierRegisteringCountChapar,
      } = await ordersRepository.getOrdersCount({
        ...payload.courierRegisteringCountChapar,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierRegisterdCountChapar,
      } = await ordersRepository.getOrdersCount({
        ...payload.courierRegisterdCountChapar,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierDeliveredCountChapar,
      } = await ordersRepository.getOrdersCount({
        ...payload.courierDeliveredCountChapar,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierDelivered3plCountChapar,
      } = await ordersRepository.getOrdersCount({
        ...payload.courierDelivered3plCountChapar,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierNotDeliverdCountChapar,
      } = await ordersRepository.getOrdersCount({
        ...payload.courierNotDeliverdCountChapar,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierReturnedCountChapar,
      } = await ordersRepository.getOrdersCount({
        ...payload.courierReturnedCountChapar,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierRegisteringCountPishro,
      } = await ordersRepository.getOrdersCount({
        ...payload.courierRegisteringCountPishro,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierRegisterdCountPishro,
      } = await ordersRepository.getOrdersCount({
        ...payload.courierRegisterdCountPishro,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierDeliveredCountPishro,
      } = await ordersRepository.getOrdersCount({
        ...payload.courierDeliveredCountPishro,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierDelivered3plCountPishro,
      } = await ordersRepository.getOrdersCount({
        ...payload.courierDelivered3plCountPishro,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierNotDeliverdCountPishro,
      } = await ordersRepository.getOrdersCount({
        ...payload.courierNotDeliverdCountPishro,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierReturnedCountPishro,
      } = await ordersRepository.getOrdersCount({
        ...payload.courierReturnedCountPishro,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierBundledCount,
      } = await ordersRepository.getOrdersCount({
        ...payload.courierBundledCount,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierLoadedCount,
      } = await ordersRepository.getOrdersCount({
        ...payload.courierLoadedCount,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierDeliveringCount,
      } = await ordersRepository.getOrdersCount({
        ...payload.courierDeliveringCount,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierNotDeliverdCount,
      } = await ordersRepository.getOrdersCount({
        ...payload.courierNotDeliverdCount,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierReturnedCount,
      } = await ordersRepository.getOrdersCount({
        ...payload.courierReturnedCount,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierDeliveringPostCount,
      } = await ordersRepository.getPostOrdersCount({
        ...payload.courierDeliveringPostCount,
        'fulfillmentWarehouseId.equals': 4,
      });
      const {
        response: courierDeliveredPostCount,
      } = await ordersRepository.getPostOrdersCount({
        ...payload.courierDeliveredPostCount,
        'fulfillmentWarehouseId.equals': 4,
      });

      setCardCounts({
        readyCount,
        readyCountPost,
        readyCountVip,
        createCount,
        createCountPost,
        createCountVip,
        packagedCountVip,
        packagedCount,
        packagedCountPost,
        courierRegisteringCountTarof,
        courierRegisterdCountTarof,
        courierDeliveredCountTarof,
        courierDelivered3plCountTarof,
        courierNotDeliverdCountTarof,
        courierReturnedCountTarof,
        courierRegisteringCountLink,
        courierRegisterdCountLink,
        courierDeliveredCountLink,
        courierDelivered3plCountLink,
        courierNotDeliverdCountLink,
        courierReturnedCountLink,
        courierRegisteringCountPetro,
        courierRegisterdCountPetro,
        courierDeliveredCountPetro,
        courierDelivered3plCountPetro,
        courierNotDeliverdCountPetro,
        courierReturnedCountPetro,
        courierRegisteringCountChapar,
        courierRegisterdCountChapar,
        courierDeliveredCountChapar,
        courierDelivered3plCountChapar,
        courierNotDeliverdCountChapar,
        courierReturnedCountChapar,
        courierRegisteringCountPishro,
        courierRegisterdCountPishro,
        courierDeliveredCountPishro,
        courierDelivered3plCountPishro,
        courierNotDeliverdCountPishro,
        courierReturnedCountPishro,
        courierBundledCount,
        courierLoadedCount,
        courierDeliveringCount,
        courierNotDeliverdCount,
        courierReturnedCount,
        courierDeliveringPostCount,
        courierDeliveredPostCount,
      });
    } catch (error) {
      setCountError(error.message);
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  };

  const request = {
    readyCount: {
      'status.in': ['ASSIGNED', 'PICKED'],
      'deliveryType.in': ['COURIER', 'CHAPAR'],
    },
    readyCountPost: {
      'orderStatus.in': ['ASSIGNED', 'PICKED'],
    },
    readyCountVip: {
      'status.in': ['ASSIGNED', 'PICKED'],
      'deliveryType.equals': ['VIP'],
    },
    createCount: {
      'status.in': ['ORDERED'],
      'deliveryType.in': ['COURIER', 'CHAPAR'],
    },
    createCountPost: {
      'orderStatus.in': ['ORDERED'],
    },
    createCountVip: {
      'status.in': ['ORDERED'],
      'deliveryType.equals': ['VIP'],
    },
    packagedCount: {
      'status.in': ['PACKAGED'],
      'deliveryType.in': ['COURIER', 'CHAPAR'],
    },
    packagedCountPost: {
      'orderStatus.in': ['PACKAGED'],
    },
    packagedCountVip: {
      'status.in': ['PACKAGED'],
      'deliveryType.equals': ['VIP'],
    },
    courierRegisteringCountTarof: {
      'status.in': ['REGISTERING_IN_3PL'],
      'tplType.in': ['TAROFF_V2', 'TAROFF'],
    },
    courierRegisterdCountTarof: {
      'status.in': ['REGISTERED_IN_3PL'],
      'tplType.in': ['TAROFF_V2', 'TAROFF'],
    },
    courierDeliveredCountTarof: {
      'status.in': ['DELIVERED_TO_COURIER'],
      'tplType.in': ['TAROFF_V2', 'TAROFF'],
    },
    courierDelivered3plCountTarof: {
      'status.in': ['DELIVERED_TO_3PL_COURIER'],
      'tplType.in': ['TAROFF_V2', 'TAROFF'],
    },
    courierNotDeliverdCountTarof: {
      'status.in': ['NOT_DELIVERED'],
      'tplType.in': ['TAROFF_V2', 'TAROFF'],
    },
    courierReturnedCountTarof: {
      'status.in': ['RETURNED'],
      'tplType.in': ['TAROFF_V2', 'TAROFF'],
    },
    courierRegisteringCountLink: {
      'status.in': ['REGISTERING_IN_3PL'],
      'tplType.equals': ['LINK_EXPRESS'],
    },
    courierRegisterdCountLink: {
      'status.in': ['REGISTERED_IN_3PL'],
      'tplType.equals': ['LINK_EXPRESS'],
    },
    courierDeliveredCountLink: {
      'status.in': ['DELIVERED_TO_COURIER'],
      'tplType.equals': ['LINK_EXPRESS'],
    },
    courierDelivered3plCountLink: {
      'status.in': ['DELIVERED_TO_3PL_COURIER'],
      'tplType.equals': ['LINK_EXPRESS'],
    },
    courierNotDeliverdCountLink: {
      'status.in': ['NOT_DELIVERED'],
      'tplType.equals': ['LINK_EXPRESS'],
    },
    courierReturnedCountLink: {
      'status.in': ['RETURNED'],
      'tplType.equals': ['LINK_EXPRESS'],
    },

    courierRegisteringCountPetro: {
      'status.in': ['REGISTERING_IN_3PL'],
      'tplType.equals': ['PETROEX'],
    },
    courierRegisterdCountPetro: {
      'status.in': ['REGISTERED_IN_3PL'],
      'tplType.equals': ['PETROEX'],
    },
    courierDeliveredCountPetro: {
      'status.in': ['DELIVERED_TO_COURIER'],
      'tplType.equals': ['PETROEX'],
    },
    courierDelivered3plCountPetro: {
      'status.in': ['DELIVERED_TO_3PL_COURIER'],
      'tplType.equals': ['PETROEX'],
    },
    courierNotDeliverdCountPetro: {
      'status.in': ['NOT_DELIVERED'],
      'tplType.equals': ['PETROEX'],
    },
    courierReturnedCountPetro: {
      'status.in': ['RETURNED'],
      'tplType.equals': ['PETROEX'],
    },
    courierRegisteringCountChapar: {
      'status.in': ['REGISTERING_IN_3PL'],
      'tplType.equals': ['CHAPAR'],
    },
    courierRegisterdCountChapar: {
      'status.in': ['REGISTERED_IN_3PL'],
      'tplType.equals': ['CHAPAR'],
    },
    courierDeliveredCountChapar: {
      'status.in': ['DELIVERED_TO_COURIER'],
      'tplType.equals': ['CHAPAR'],
    },
    courierDelivered3plCountChapar: {
      'status.in': ['DELIVERED_TO_3PL_COURIER'],
      'tplType.equals': ['CHAPAR'],
    },
    courierNotDeliverdCountChapar: {
      'status.in': ['NOT_DELIVERED'],
      'tplType.equals': ['CHAPAR'],
    },
    courierReturnedCountChapar: {
      'status.in': ['RETURNED'],
      'tplType.equals': ['CHAPAR'],
    },
    courierRegisteringCountPishro: {
      'status.in': ['REGISTERING_IN_3PL'],
      'tplType.equals': ['PISHRO_POST'],
    },
    courierRegisterdCountPishro: {
      'status.in': ['REGISTERED_IN_3PL'],
      'tplType.equals': ['PISHRO_POST'],
    },
    courierDeliveredCountPishro: {
      'status.in': ['DELIVERED_TO_COURIER'],
      'tplType.equals': ['PISHRO_POST'],
    },
    courierDelivered3plCountPishro: {
      'status.in': ['DELIVERED_TO_3PL_COURIER'],
      'tplType.equals': ['PISHRO_POST'],
    },
    courierNotDeliverdCountPishro: {
      'status.in': ['NOT_DELIVERED'],
      'tplType.equals': ['PISHRO_POST'],
    },
    courierReturnedCountPishro: {
      'status.in': ['RETURNED'],
      'tplType.equals': ['PISHRO_POST'],
    },
    courierBundledCount: {
      'status.in': ['BUNDLED'],
      'deliveryType.in': ['COURIER,CHAPAR'],
    },
    courierLoadedCount: {
      'status.in': ['LOADED'],
      'deliveryType.in': ['COURIER,CHAPAR'],
    },
    courierDeliveringCount: {
      'status.in': ['DELIVERING'],
      'deliveryType.in': ['COURIER,CHAPAR'],
    },
    courierNotDeliverdCount: {
      'status.in': ['NOT_DELIVERED'],
      'deliveryType.in': ['COURIER,CHAPAR'],
    },
    courierReturnedCount: {
      'status.in': ['RETURNED'],
      'deliveryType.in': ['COURIER,CHAPAR'],
    },
    courierDeliveringPostCount: {
      'orderStatus.in': ['DELIVERING_TO_POST'],
    },
    courierDeliveredPostCount: {
      'orderStatus.in': ['DELIVERED_TO_POST'],
    },
  };

  useEffect(() => {
    getingCounts(request);
    setupFilters();
    const storedId = JSON.parse(localStorage.getItem('id'));
    setId(storedId);
  }, [filtersValues]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const storedId = JSON.parse(localStorage.getItem('id'));
    if (storedId) {
      localStorage.removeItem('id');
    }
    localStorage.setItem('id', JSON.stringify(id));
  }, [id]);

  const classes = useStyles();

  const setupFilters = () => {
    setFilters([
      {
        type: 'input',
        id: 'customerCity.contains',
        label: 'شهر',
        icon: mdiCityVariantOutline,
      },
      {
        type: 'input',
        id: 'customerName.contains',
        label: 'نام سفارش‌دهنده',
        icon: mdiAccountOutline,
      },
      {
        type: 'input',
        id: 'id.equals',
        label: 'شناسه سفارش',
        icon: mdiPackageVariant,
      },
      {
        type: 'input',
        id: 'trackingCode.contains',
        label: 'کد پیگیری',
        icon: mdiKey,
      },
      {
        type: 'input',
        id: 'partnerName.contains',
        label: 'شرکت',
        icon: mdiCity,
      },
      {
        type: 'input',
        id: 'sourceName.contains',
        label: 'مبدا (انبار/مرکز)',
        icon: mdiWarehouse,
      },
      {
        type: 'date-picker-time',
        id: 'created.greaterThanOrEqual',
        label: 'تاریخ ثبت سفارش از ',
        icon: mdiCalendar,
      },
      {
        type: 'date-picker-time',
        id: 'created.lessThanOrEqual',
        label: 'تا تاریخ ثبت سفارش  ',
        icon: mdiCalendar,
      },
      {
        type: 'date-picker',
        id: 'deliveryDate.greaterThanOrEqual',
        label: 'تاریخ تحویل از ',
        icon: mdiCalendar,
      },
      {
        type: 'date-picker',
        id: 'deliveryDate.lessThanOrEqual',
        label: '  تا تاریخ تحویل ',
        icon: mdiCalendar,
      },
      {
        type: 'checkbox',
        id: 'status.in',
        label: 'وضعیت سفارش',
        icon: mdiStateMachine,
        data: [
          'ORDERED',
          'DELIVERED_TO_POST',
          'DELIVERED_TO_CENTER',
          'SUPPLIED_IN_CENTER',
          'PACKAGED',
          'BUNDLED',
          'LOADED',
          'ARRIVED',
          'DELIVERED',
          'NOT_DELIVERED',
          'RETURNED',
          'CANCELLED',
          'REGISTERED_IN_3PL',
          'ASSIGNED'
        ],
        translateType: 'Order',
      },
      {
        type: 'select',
        id: 'bundleId.specified',
        label: 'وضعیت بسته',
        data: ['false', 'true'],
        translateType: 'OrderBundleStatus',
        icon: mdiPackageVariant,
      },
      {
        type: 'checkbox',
        id: 'tplType.in',
        label: 'نوع 3PL',
        icon: mdiTruck,
        data: [
          'NONE',
          'LINK_EXPRESS',
          'ALOPEYK',
          'TAROFF_V2',
          'CHAPAR',
          'PETROEX',
        ],
        translateType: 'TPL',
      },
    ]);
  };

  const handleCardClick = (name: string) => {
    setFiltersValues({
      ...request[name],
      page: 0,
      size: 100,
    });
  };

  const pieOptions = {
    slices: [
      {
        color: '#5CBAFF',
      },
      {
        color: '#F9D749',
      },
      {
        color: '#783CF8',
      },
    ],
    legend: {
      position: 'none',
      alignment: 'center',
      textStyle: {
        color: '233238',
        fontSize: 14,
      },
    },
    tooltip: {
      showColorCode: false,
    },
    chartArea: {
      left: 0,
      top: '8px',
      width: '100%',
      height: '90%',
    },
  };

  const location = useLocation();

  return (
    <AuthConsumer>
      {({ user }) => (
        <div className={classes.root}>
          <Grid container spacing={5}>
            <Grid container item xs={12} sm={6} alignItems="center">
              <Typography
                variant="h4"
                component="div"
                style={{ marginLeft: '20px', fontSize: '18px' }}
              >
                {' سفارش‌ ها '}
              </Typography>
            </Grid>
            <Grid container item sm={12} direction="row-reverse" spacing={4}>
              <RenderFilter
                filters={filters}
                onFilter={(filtervalues: any) => {
                  setFiltersValues({
                    ...removeEmptyValueObject(filtervalues),
                    'deliveryType.equals': '',
                    page: 0,
                    size: 100,
                  });
                }}
                mainFiltersCount={1}
                baseFilter={{
                  type: 'input',
                  id: 'id.equals',
                  placeholder: 'شناسه سفارش',
                }}
                filterValues={filtersValues}
              />
            </Grid>

            <Grid container item spacing={2} md={12}>
              <Grid container item md={4}>
                <Card style={{ width: '100%' }}>
                  <CardHeader
                    style={{ width: '100%', padding: '16px 20px' }}
                    component="div"
                    className={classes.headerWidth}
                    avatar={
                      <Grid
                        style={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <div>
                          <Typography>لجستیک</Typography>
                        </div>
                        <div>
                          <Typography>
                            {cardCounts.createCount?.data +
                              cardCounts.readyCount?.data +
                              cardCounts.packagedCount?.data}{' '}
                            سفارش
                          </Typography>
                        </div>
                      </Grid>
                    }
                  />
                  <CardContent>
                    <Divider
                      style={{
                        width: '110%',
                        margin: '-20px -16px 8px 8px',
                      }}
                    />

                    <Grid container md={12} style={{ marginTop: '16px' }}>
                      <Chart
                        width={'150px'}
                        height={'160px'}
                        chartType="PieChart"
                        options={pieOptions}
                        loader={<div>Loading Chart</div>}
                        data={[
                          ['تعداد', 'کل سفارش ها'],
                          ['ایجاد شده', cardCounts.createCount?.data],
                          ['درحال آماده سازی', cardCounts.readyCount?.data],
                          ['آماده ارسال', cardCounts.packagedCount?.data],
                        ]}
                      />
                      <Grid md={6} item>
                        <Button
                          onClick={name => handleCardClick('createCount')}
                          variant="outlined"
                          style={{ margin: '4px' }}
                        >
                          <span
                            className={classes.colorBullet}
                            style={{
                              backgroundColor: '#5CBAFF',
                            }}
                          ></span>
                          {`ایجاد شده | ${cardCounts.createCount?.data}`}
                        </Button>
                        <Button
                          onClick={name => handleCardClick('readyCount')}
                          variant="outlined"
                          style={{ margin: '4px' }}
                        >
                          {' '}
                          <span
                            className={classes.colorBullet}
                            style={{
                              backgroundColor: '#F9D749',
                            }}
                          ></span>
                          {`درحال آماده سازی  | ${cardCounts.readyCount?.data}`}
                        </Button>
                        <Button
                          onClick={name => handleCardClick('packagedCount')}
                          variant="outlined"
                          style={{ margin: '4px' }}
                        >
                          <span
                            className={classes.colorBullet}
                            style={{
                              backgroundColor: '#783CF8',
                            }}
                          ></span>
                          {`آماده ارسال  | ${cardCounts.packagedCount?.data}`}
                        </Button>
                      </Grid>
                    </Grid>
                    <Divider style={{ width: '100%', marginTop: '8px' }} />
                  </CardContent>
                  <CardActions>
                    <Button
                      style={{
                        marginRight: 'auto',
                        marginBottom: '8px',
                        marginLeft: '8px',
                      }}
                      onClick={
                        filtersValues['status.in']
                          ? () =>
                            props.history.push(
                              `/nonPost-orders${location.search}`
                            )
                          : () =>
                            props.history.push(
                              `/nonPost-orders?page=0&size=10&status.in=ORDERED,PACKAGED,ASSIGNED,PICKED&deliveryType.in=COURIER,CHAPAR&sort=created,desc`
                            )
                      }
                      variant="contained"
                      color="primary"
                    >
                      مشاهده
                      <Icon path={mdiChevronLeft} size={1} />
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
              <Grid container item md={4}>
                <Card style={{ width: '100%' }}>
                  <CardHeader
                    style={{ width: '100%', padding: '16px 20px' }}
                    component="div"
                    className={classes.headerWidth}
                    avatar={
                      <Grid
                        style={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <div>
                          <Typography>پست</Typography>
                        </div>
                        <div>
                          <Typography>
                            {cardCounts.createCountPost?.data +
                              cardCounts.readyCountPost?.data +
                              cardCounts.packagedCountPost?.data}{' '}
                            سفارش
                          </Typography>
                        </div>
                      </Grid>
                    }
                  />
                  <CardContent>
                    <Divider
                      style={{
                        width: '110%',
                        margin: '-20px -16px 8px 8px',
                      }}
                    />

                    <Grid container md={12} style={{ marginTop: '16px' }}>
                      <Chart
                        width={'150px'}
                        height={'150px'}
                        chartType="PieChart"
                        options={pieOptions}
                        loader={<div>Loading Chart</div>}
                        data={[
                          ['تعداد', 'کل سفارش ها'],
                          ['ایجاد شده', cardCounts.createCountPost?.data],
                          ['درحال آماده سازی', cardCounts.readyCountPost?.data],
                          ['آماده ارسال', cardCounts.packagedCountPost?.data],
                        ]}
                      />
                      <Grid md={6} item>
                        <Button
                          onClick={name => handleCardClick('createCountPost')}
                          variant="outlined"
                          style={{ margin: '4px' }}
                        >
                          <span
                            className={classes.colorBullet}
                            style={{
                              backgroundColor: '#5CBAFF',
                            }}
                          ></span>
                          {`ایجاد شده | ${cardCounts.createCountPost?.data}`}
                        </Button>
                        <Button
                          onClick={name => handleCardClick('readyCountPost')}
                          variant="outlined"
                          style={{ margin: '4px' }}
                        >
                          <span
                            className={classes.colorBullet}
                            style={{
                              backgroundColor: '#F9D749',
                            }}
                          ></span>
                          {`درحال آماده سازی  | ${cardCounts.readyCountPost?.data}`}
                        </Button>
                        <Button
                          onClick={name => handleCardClick('packagedCountPost')}
                          variant="outlined"
                          style={{ margin: '4px' }}
                        >
                          <span
                            className={classes.colorBullet}
                            style={{
                              backgroundColor: '#783CF8',
                            }}
                          ></span>
                          {`آماده ارسال  | ${cardCounts.packagedCountPost?.data}`}
                        </Button>
                      </Grid>
                    </Grid>
                    <Divider style={{ width: '100%', marginTop: '8px' }} />
                  </CardContent>
                  <CardActions>
                    <Button
                      onClick={
                        filtersValues['orderStatus.in']
                          ? () =>
                            props.history.push(
                              `/post-orders${location.search}`
                            )
                          : () =>
                            props.history.push(
                              `/post-orders?page=0&size=10&orderStatus.in=ORDERED,PACKAGED,ASSIGNED,PICKED&sort=created,desc`
                            )
                      }
                      style={{
                        marginRight: 'auto',
                        marginBottom: '8px',
                        marginLeft: '8px',
                      }}
                      variant="contained"
                      color="primary"
                    >
                      مشاهده
                      <Icon path={mdiChevronLeft} size={1} />
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
              <Grid container item md={4}>
                <Card style={{ width: '100%' }}>
                  <CardHeader
                    style={{ width: '100%', padding: '16px 20px' }}
                    component="div"
                    className={classes.headerWidth}
                    avatar={
                      <Grid
                        style={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <div>
                          <Typography>ارسال ویژه</Typography>
                        </div>
                        <div>
                          <Typography>
                            {cardCounts.createCountVip?.data +
                              cardCounts.readyCountVip?.data +
                              cardCounts.packagedCountVip?.data}{' '}
                            سفارش
                          </Typography>
                        </div>
                      </Grid>
                    }
                  />
                  <CardContent>
                    <Divider
                      style={{
                        width: '110%',
                        margin: '-20px -16px 8px 8px',
                      }}
                    />

                    <Grid container md={12} style={{ marginTop: '16px' }}>
                      <Chart
                        width={'150px'}
                        height={'150px'}
                        chartType="PieChart"
                        options={pieOptions}
                        loader={<div>Loading Chart</div>}
                        data={[
                          ['تعداد', 'کل سفارش ها'],
                          ['ایجاد شده', cardCounts.createCountVip?.data],
                          ['درحال آماده سازی', cardCounts.readyCountVip?.data],
                          ['آماده ارسال', cardCounts.packagedCountVip?.data],
                        ]}
                      />
                      <Grid md={6} item>
                        <Button
                          onClick={name => handleCardClick('createCountVip')}
                          variant="outlined"
                          style={{ margin: '4px' }}
                        >
                          <span
                            className={classes.colorBullet}
                            style={{
                              backgroundColor: '#5CBAFF',
                            }}
                          ></span>
                          {`ایجاد شده | ${cardCounts.createCountVip?.data}`}
                        </Button>
                        <Button
                          onClick={name => handleCardClick('readyCountVip')}
                          variant="outlined"
                          style={{ margin: '4px' }}
                        >
                          <span
                            className={classes.colorBullet}
                            style={{
                              backgroundColor: '#F9D749',
                            }}
                          ></span>
                          {`درحال آماده سازی  | ${cardCounts.readyCountVip?.data}`}
                        </Button>
                        <Button
                          onClick={name => handleCardClick('packagedCountVip')}
                          variant="outlined"
                          style={{ margin: '4px' }}
                        >
                          <span
                            className={classes.colorBullet}
                            style={{
                              backgroundColor: '#783CF8',
                            }}
                          ></span>
                          {`آماده ارسال  | ${cardCounts.packagedCountVip?.data}`}
                        </Button>
                      </Grid>
                    </Grid>
                    <Divider style={{ width: '100%', marginTop: '8px' }} />
                  </CardContent>
                  <CardActions>
                    <Button
                      onClick={
                        filtersValues['status.in']
                          ? () =>
                            props.history.push(
                              `/vip-orders${location.search}`
                            )
                          : () =>
                            props.history.push(
                              `/vip-orders?page=0&size=10&status.in=ORDERED,PACKAGED,ASSIGNED,PICKED&deliveryType.equals=VIP&sort=created,desc`
                            )
                      }
                      style={{
                        marginRight: 'auto',
                        marginBottom: '8px',
                        marginLeft: '8px',
                      }}
                      variant="contained"
                      color="primary"
                    >
                      مشاهده
                      <Icon path={mdiChevronLeft} size={1} />
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
              <Grid container item md={2}>
                <CourierCards
                  history={props.history}
                  handleCardClick={handleCardClick}
                  counts={[
                    cardCounts.courierRegisteringCountTarof?.data,
                    cardCounts.courierRegisterdCountTarof?.data,
                    cardCounts.courierDeliveredCountTarof?.data,
                    cardCounts.courierDelivered3plCountTarof?.data,
                    cardCounts.courierNotDeliverdCountTarof?.data,
                    cardCounts.courierReturnedCountTarof?.data,
                  ]}
                  name="تعارف"
                  iconUrl="tarof.svg"
                  cardName="Tarof"
                  loginUrl={
                    filtersValues['tplType.equals'] !== 'TAROFF'
                      ? `/nonPost-orders?status.in=REGISTERED_IN_3PL,REGISTERING_IN_3PL,DELIVERED_TO_COURIER,DELIVERED_TO_3PL_COURIER,NOT_DELIVERED,RETURNED&tplType.equals=TAROFF&page=0&size=100&sort=created,desc`
                      : `/nonPost-orders${location.search}`
                  }
                  cardType="3pl"
                />
              </Grid>
              <Grid container item md={2}>
                <CourierCards
                  history={props.history}
                  handleCardClick={handleCardClick}
                  counts={[
                    cardCounts.courierRegisteringCountLink?.data,
                    cardCounts.courierRegisterdCountLink?.data,
                    cardCounts.courierDeliveredCountLink?.data,
                    cardCounts.courierDelivered3plCountLink?.data,
                    cardCounts.courierNotDeliverdCountLink?.data,
                    cardCounts.courierReturnedCountLink?.data,
                  ]}
                  name="لینک اکسپرس"
                  iconUrl="link.svg"
                  cardName="Link"
                  loginUrl={
                    filtersValues['tplType.equals'] !== 'LINK_EXPRESS'
                      ? `/nonPost-orders?status.in=REGISTERED_IN_3PL,REGISTERING_IN_3PL,DELIVERED_TO_COURIER,DELIVERED_TO_3PL_COURIER,NOT_DELIVERED,RETURNED&tplType.equals=LINK_EXPRESS&page=0&size=100&sort=created,desc`
                      : `/nonPost-orders${location.search}`
                  }
                  cardType="3pl"
                />
              </Grid>
              <Grid container item md={2}>
                <CourierCards
                  history={props.history}
                  handleCardClick={handleCardClick}
                  counts={[
                    cardCounts.courierRegisteringCountPetro?.data,
                    cardCounts.courierRegisterdCountPetro?.data,
                    cardCounts.courierDeliveredCountPetro?.data,
                    cardCounts.courierDelivered3plCountPetro?.data,
                    cardCounts.courierNotDeliverdCountPetro?.data,
                    cardCounts.courierReturnedCountPetro?.data,
                  ]}
                  name="پترواکس"
                  iconUrl="petro.svg"
                  cardName="Petro"
                  loginUrl={
                    filtersValues['tplType.equals'] !== 'PETROEX'
                      ? `/nonPost-orders?status.in=REGISTERED_IN_3PL,REGISTERING_IN_3PL,DELIVERED_TO_COURIER,DELIVERED_TO_3PL_COURIER,NOT_DELIVERED,RETURNED&tplType.equals=PETROEX&page=0&size=100&sort=created,desc`
                      : `/nonPost-orders${location.search}`
                  }
                  cardType="3pl"
                />
              </Grid>
              <Grid container item md={2}>
                <CourierCards
                  history={props.history}
                  handleCardClick={handleCardClick}
                  counts={[
                    cardCounts.courierRegisteringCountChapar?.data,
                    cardCounts.courierRegisterdCountChapar?.data,
                    cardCounts.courierDeliveredCountChapar?.data,
                    cardCounts.courierDelivered3plCountChapar?.data,
                    cardCounts.courierNotDeliverdCountChapar?.data,
                    cardCounts.courierReturnedCountChapar?.data,
                  ]}
                  name="چاپار"
                  iconUrl="chapar3.svg"
                  cardName="Chapar"
                  loginUrl={
                    filtersValues['tplType.equals'] !== 'CHAPAR'
                      ? `/nonPost-orders?status.in=REGISTERED_IN_3PL,REGISTERING_IN_3PL,DELIVERED_TO_COURIER,DELIVERED_TO_3PL_COURIER,NOT_DELIVERED,RETURNED&tplType.equals=CHAPAR&page=0&size=100&sort=created,desc`
                      : `/nonPost-orders${location.search}`
                  }
                  cardType="3pl"
                />
              </Grid>
              <Grid container item md={2}>
                <CourierCards
                  history={props.history}
                  handleCardClick={handleCardClick}
                  counts={[
                    cardCounts.courierRegisteringCountPishro?.data,
                    cardCounts.courierRegisterdCountPishro?.data,
                    cardCounts.courierDeliveredCountPishro?.data,
                    cardCounts.courierDelivered3plCountPishro?.data,
                    cardCounts.courierNotDeliverdCountPishro?.data,
                    cardCounts.courierReturnedCountPishro?.data,
                  ]}
                  name="پیشرو پست"
                  iconUrl="pishro.svg"
                  cardName="Pishro"
                  loginUrl={
                    filtersValues['tplType.equals'] !== 'PISHRO_POST'
                      ? `/nonPost-orders?status.in=REGISTERED_IN_3PL,REGISTERING_IN_3PL,DELIVERED_TO_COURIER,DELIVERED_TO_3PL_COURIER,NOT_DELIVERED,RETURNED&tplType.equals=PISHRO_POST&page=0&size=100&sort=created,desc`
                      : `/nonPost-orders${location.search}`
                  }
                  cardType="3pl"
                />
              </Grid>
              <Grid container item md={2}>
                <CourierCards
                  history={props.history}
                  handleCardClick={handleCardClick}
                  counts={[
                    cardCounts.courierBundledCount?.data,
                    cardCounts.courierLoadedCount?.data,
                    cardCounts.courierDeliveringCount?.data,
                    cardCounts.courierNotDeliverdCount?.data,
                    cardCounts.courierReturnedCount?.data,
                  ]}
                  name="ناوگان"
                  iconUrl="ronad small.svg"
                  cardName="Bundle"
                  loginUrl={
                    filtersValues['deliveryType.in'] !== 'COURIER,CHAPAR'
                      ? `/nonPost-orders?status.in=BUNDLED,LOADED,DELIVERING,NOT_DELIVERED,RETURNED&deliveryType.in=COURIER,CHAPAR&page=0&size=100&sort=created,desc`
                      : `/nonPost-orders${location.search}`
                  }
                  cardType="Bundle"
                />
              </Grid>
              <Grid container item md={2}>
                <CourierCards
                  history={props.history}
                  handleCardClick={handleCardClick}
                  counts={[
                    cardCounts.courierDeliveringPostCount?.data,
                    cardCounts.courierDeliveredPostCount?.data,
                  ]}
                  name="پست"
                  iconUrl="post.svg"
                  cardName="Post"
                  loginUrl={
                    filtersValues['orderStatus.in'] !==
                      'DELIVERED_TO_POST,DELIVERING_TO_POST'
                      ? `/post-orders?orderStatus.in=DELIVERED_TO_POST,DELIVERING_TO_POST&page=0&size=100&sort=created,desc`
                      : `/post-orders${location.search}`
                  }
                  cardType="Post"
                />
              </Grid>
            </Grid>
          </Grid>
        </div>
      )}
    </AuthConsumer>
  );
};

const mapStateToProps = createStructuredSelector<Store, SelectorProps>({
  ordersList: makeSelectOrdersList(),
  allOrders: makeSelectAllOrders(),
  count: makeSelectCountList(),

  loading: makeSelectLoading(),
  error: makeSelectError(),
});

function mapDispatchToProps(dispatch: Function) {
  return {
    postOrdersDispatch: (payload: Payload) =>
      dispatch(getPostOrdersAction(payload)),

    ordersDispatch: (payload: Payload) => dispatch(getOrdersAction(payload)),
    allOrdersDispatch: (payload: Payload) =>
      dispatch(getAllOrdersReportAction(payload)),
    updateOrderStatusDispatch: (payload: {
      id: number;
      body: string;
      pagination: object;
    }) => dispatch(updateOrderStatusActions(payload)),
    updateAllOrderStatusDispatch: (payload: PayloadAllUpdate) =>
      dispatch(updateAllOrderStatusActions(payload)),
    cancelOrderDispatch: (payload: cancelPayload) =>
      dispatch(cancelOrderAction(payload)),
    changeDeliverTypeDispatch: (payload: PayloadChangeDeliveryType) =>
      dispatch(changeDeliveryTypeAction(payload)),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);
// @ts-ignore
export default compose(withConnect)(AllOrders);
